import type {ExtraDeliveryDetailsData} from '../types';

import {dateToReadableDate} from '@date-picker';
import {Button, Loading} from '@ds/components';
import {DetailPageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  deleteExtraDelivery,
  getExtraDeliveryDetail,
  pauseExtraDelivery
} from 'store/slices/extra-delivery/thunk';

import ExtraDeliveryInfoRow from '../components/common/ExtraDeliveryInfoRow/ExtraDeliveryInfoRow';
import ExtraDeliveryStatus from '../components/common/ExtraDeliveryStatus/ExtraDeliveryStatus';
import {EXTRA_DELIVERY_ROUTES} from '../constants';
import {getExtraDeliveryVendorId, normalizeExtraDeliveryState} from '../utils';
import ExtraDeliveryActionModal from './components/ExtraDeliveryActionModal/ExtraDeliveryActionModal';

const ExtraDeliveryDetails: React.FC = () => {
  const vendorId = getExtraDeliveryVendorId();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {extraDeliveryId} = useParams();
  const {details, detailLoading, remove, pause} = useAppSelector((state) => state.extraDelivery);
  const campaign = details as ExtraDeliveryDetailsData | null;
  const normalizedState = normalizeExtraDeliveryState(campaign?.state);
  const canAct =
    normalizedState === 'init' || normalizedState === 'active' || normalizedState === 'paused';

  useEffect(() => {
    void dispatch(
      getExtraDeliveryDetail({vendor_id: vendorId, id: Number(extraDeliveryId)})
    );
  }, [dispatch, vendorId, extraDeliveryId]);

  const pauseCampaign = () => {
    if (!campaign?.id) {
      return;
    }

    void dispatch(pauseExtraDelivery({id: campaign.id, vendor_id: vendorId, pause: true}))
      .then(() => {
        setIsConfirmOpen(false);
        navigate(EXTRA_DELIVERY_ROUTES.list);
      })
      .catch((error: {data?: {errors?: {message?: string}}}) => {
        toast(error.data?.errors?.message || 'مشکلی پیش آمده. دوباره تلاش کنید!');
      });
  };

  const resumeCampaign = () => {
    if (!campaign?.id) {
      return;
    }

    void dispatch(pauseExtraDelivery({id: campaign.id, vendor_id: vendorId, pause: false}))
      .then(() => {
        setIsConfirmOpen(false);
        void dispatch(getExtraDeliveryDetail({vendor_id: vendorId, id: campaign.id}));
      })
      .catch((error: {data?: {errors?: {message?: string}}}) => {
        toast(error.data?.errors?.message || 'مشکلی پیش آمده. دوباره تلاش کنید!');
      });
  };

  const deleteCampaign = () => {
    if (!campaign?.id) {
      return;
    }

    void dispatch(deleteExtraDelivery({id: campaign.id, vendor_id: vendorId}))
      .then(() => {
        setIsConfirmOpen(false);
        navigate(EXTRA_DELIVERY_ROUTES.list);
      })
      .catch((error: {data?: {errors?: {message?: string}}}) => {
        toast(error.data?.errors?.message || 'مشکلی پیش آمده. دوباره تلاش کنید!');
      });
  };

  const confirmAction =
    normalizedState === 'init'
      ? deleteCampaign
      : normalizedState === 'active'
        ? pauseCampaign
        : resumeCampaign;

  if (detailLoading) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  return (
    <>
      <DetailPageLayout
        contentClassName="px-4"
        footer={
          canAct ? (
            <Button
              fullWidth
              size="lg"
              variant="fill"
              color={normalizedState === 'init' ? 'destructive' : 'primary'}
              onClick={() => setIsConfirmOpen(true)}
            >
              {normalizedState === 'init' ? 'حذف' : normalizedState === 'active' ? 'توقف' : 'ادامه'}
            </Button>
          ) : undefined
        }
      >
        <ExtraDeliveryInfoRow title="وضعیت" value={<ExtraDeliveryStatus value={campaign?.state} />} />
        <ExtraDeliveryInfoRow
          title="تاریخ شروع"
          value={dateToReadableDate({
            date: campaign?.start_date ?? '',
            day: true,
            month: true,
            year: true
          })}
        />
        <ExtraDeliveryInfoRow
          title="تاریخ پایان"
          value={dateToReadableDate({
            date: campaign?.end_date ?? '',
            day: true,
            month: true,
            year: true
          })}
          hasBorder={false}
        />
      </DetailPageLayout>
      <ExtraDeliveryActionModal
        state={normalizedState}
        isOpen={isConfirmOpen}
        loading={Boolean(remove.loading || pause.loading)}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmAction}
      />
    </>
  );
};

export default ExtraDeliveryDetails;
